import { Trans } from '@lingui/react/macro';
import { useCallback } from 'react';
import { ExternalLink, Eye } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@src/components/ui/hover-card';
import { Button } from '@src/components/ui/button';
import { Layout } from '@sdk';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { isHubOnly } from '@src/navigation/hub-runtime';
import { openWikiModal } from './wiki-modal';

interface WikiLabelProps {
  /** The wiki page name this label points at (e.g. "Welcome"). */
  wikiword: string;
  /** Display text for the button. Defaults to the wiki word. */
  label?: string;
  /**
   * Optional heading slug (e.g. "auto-run") to deep-link into a section of the
   * page. Both "Open" and "Preview" scroll to it once the markdown renders.
   */
  fragment?: string;
  /**
   * Wiki space to resolve the word in. Omitted means the `@local` alias — the
   * ACTIVE project's wiki. Pass an explicit space for a shipped system doc,
   * which lives in the assistant project and is invisible from anywhere else
   * (see `useAssistantWikiSpace`).
   */
  space?: string;
}

/**
 * A wiki word rendered as a clickable label with a *wikitip*: clicking opens
 * the wiki page (full view); hovering reveals a tip whose "Preview" pops the
 * same page in a modal via `openWikiModal`. Both directions resolve the page
 * by name. See docs/wikitip.md.
 */
export function WikiLabel({ wikiword, label, fragment, space }: WikiLabelProps) {
  const { navigation } = useDockNavigation();
  const previewWiki = useCallback(() => openWikiModal(wikiword, space, fragment), [wikiword, space, fragment]);
  // The hub page has no /dock/assets/wiki route (it redirects home), so hub
  // mode opens every wiki surface in the modal instead.
  const openWiki = useCallback(
    () =>
      isHubOnly()
        ? openWikiModal(wikiword, space, fragment)
        : navigation.openDock(DockPointer.forWiki(wikiword, Layout.DOCK, space, fragment)),
    [navigation, wikiword, space, fragment],
  );

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={openWiki}
          className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
        >
          {label ?? wikiword}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" align="start">
        <div className="flex flex-col gap-2">
          {/* `[[Name]]` is the AUTHORING syntax for a wikilink, not a title —
              showing it here made the tip look like unrendered markup. The kind
              is a chip; the page keeps its own plain title. */}
          <div className="flex items-center gap-2">
            <span
              data-testid="wiki-tip-kind"
              className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
            >
              <Trans>Wiki</Trans>
            </span>
            <span data-testid="wiki-tip-title" className="truncate text-sm font-semibold" title={wikiword}>
              {wikiword}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            <Trans>Open the page here, or peek at it without leaving this view.</Trans>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="secondary" onClick={previewWiki}>
              <Eye className="me-1 h-3.5 w-3.5" /> <Trans>Preview</Trans>
            </Button>
            <Button size="sm" variant="ghost" onClick={openWiki}>
              <ExternalLink className="me-1 h-3.5 w-3.5" /> <Trans>Open</Trans>
            </Button>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
